"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
exports.usePickupPoints = exports.PickupPointsProvider = void 0;
var _auth = require("@ori/auth");
var _configurationManager = require("@ori/configuration-manager");
var _environment = require("@ori/environment");
var _fetching = require("@ori/fetching");
var _infinite = require("@ori/fetching/infinite");
var _globalApiClientReact = require("@ori/global-api-client-react");
var _i18n = require("@ori/i18n");
var _presentationUtils = require("@ori/presentation-utils");
var _react = require("react");
var _fetchClusteredPickupPoints = require("../api/fetchClusteredPickupPoints");
var _fetchPickupPoints = require("../api/fetchPickupPoints");
var _constants = require("../constants");
var _SearchConfigurationProvider = require("../containers/SearchConfigurationProvider");
var _checkoutDeliveryContext = require("./checkoutDeliveryContext");
var _mapContext = require("./mapContext");
var _jsxRuntime = require("react/jsx-runtime");
const roundLocation = (val)=>({
        latitude: (val === null || val === void 0 ? void 0 : val.latitude) === undefined ? 0 : Number(val.latitude.toFixed(2)),
        longitude: (val === null || val === void 0 ? void 0 : val.longitude) === undefined ? 0 : Number(val.longitude.toFixed(2))
    });
const usePickupPointsApi = function(enabled) {
    let perPage = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : 7;
    var _pointsResponse_data_, _pointsResponse_data;
    const { graphQlNetUrl } = (0, _configurationManager.useServiceUrls)([
        'graphQlNet'
    ]);
    const { language } = (0, _i18n.useLanguage)();
    const { tenant } = (0, _environment.useEnvironment)();
    const { token } = (0, _auth.useToken)();
    const { customerId, sessionId = '' } = (0, _checkoutDeliveryContext.useCheckoutDeliveryContext)();
    const { viewSettings } = (0, _SearchConfigurationProvider.useSearchConfiguration)();
    const { selectedLocation, selectedDelivery } = (0, _mapContext.useMap)();
    // Search term handling
    const [searchTerm, setSearchTerm] = (0, _react.useState)('');
    const lastSearch = (0, _react.useRef)(null);
    const [searchUsed, setSearchUsed] = (0, _react.useState)(false);
    // Use search every time it changes
    (0, _react.useEffect)(()=>setSearchUsed(!!searchTerm), [
        searchTerm
    ]);
    // Don't use search after selection changes
    (0, _react.useEffect)(()=>{
        setSearchUsed(!!searchTerm && !selectedLocation);
    // eslint-disable-next-line react-compiler/react-compiler -- manually handled
    // eslint-disable-next-line react-hooks/exhaustive-deps -- manually handled
    }, [
        selectedLocation
    ]);
    // Geocoding search params, remove after backend API fix
    const { formattedToken } = (0, _auth.useToken)();
    const { credentials, mapProvider, setViewSettings, zoomAfterSelection } = (0, _SearchConfigurationProvider.useSearchConfiguration)();
    const headers = (0, _react.useMemo)(()=>(0, _fetching.getHeaders)({
            language,
            customerId,
            sourceAppName: _constants.APP_NAME,
            tenant,
            token
        }), [
        language,
        customerId,
        tenant,
        token
    ]);
    const pointsResponse = (0, _infinite.useSWRInfinite)((pageIndex, prev)=>{
        const skip = pageIndex * perPage;
        if (!enabled || prev !== null && skip > prev.totalRecords || !viewSettings) {
            return null;
        }
        return {
            headers,
            graphQlNetUrl,
            params: {
                ...searchUsed ? {
                    searchTerm
                } : selectedLocation !== null && selectedLocation !== void 0 ? selectedLocation : roundLocation(viewSettings.location),
                sessionId,
                top: perPage,
                skip,
                radius: _constants.SEARCH_RADIUS
            },
            // Geocoding search params, remove after backend API fix
            credentials,
            mapProvider,
            language,
            formattedToken,
            tenant
        };
    }, _fetchPickupPoints.fetchPickupPoints, {
        ...(0, _presentationUtils.getRetryErrorSwrInfiniteOptions)(),
        onSuccess: (data)=>{
            var _data_;
            // Only move view on search change
            if (!searchTerm || searchTerm === lastSearch.current) {
                return;
            }
            lastSearch.current = searchTerm;
            var _data__pickupPoints_;
            const { latitude, longitude } = (_data__pickupPoints_ = (_data_ = data[0]) === null || _data_ === void 0 ? void 0 : _data_.pickupPoints[0]) !== null && _data__pickupPoints_ !== void 0 ? _data__pickupPoints_ : {};
            if (!viewSettings || latitude === undefined || longitude === undefined) {
                return;
            }
            setViewSettings({
                location: {
                    latitude,
                    longitude
                },
                zoom: zoomAfterSelection
            });
        },
        revalidateFirstPage: false
    });
    const pickupPoints = (0, _react.useMemo)(()=>{
        var _pointsResponse_data;
        return ((_pointsResponse_data = pointsResponse.data) !== null && _pointsResponse_data !== void 0 ? _pointsResponse_data : []).flatMap((page)=>page.pickupPoints);
    }, [
        pointsResponse.data
    ]);
    // Clustered pickup points handling
    const customInstance = (0, _globalApiClientReact.useApiClient)();
    const clustersResponse = (0, _fetching.useSWR)(()=>{
        if (!enabled || !viewSettings) {
            return null;
        }
        return {
            headers,
            sessionId,
            customInstance,
            ...roundLocation(viewSettings.location),
            zoom: viewSettings.zoom
        };
    }, _fetchClusteredPickupPoints.fetchClusteredPickupPoints, (0, _presentationUtils.getRetryErrorSwrOptions)());
    var _pointsResponse_data__totalRecords;
    // Infinite scroll handling
    const hasMore = ((_pointsResponse_data__totalRecords = (_pointsResponse_data = pointsResponse.data) === null || _pointsResponse_data === void 0 ? void 0 : (_pointsResponse_data_ = _pointsResponse_data[0]) === null || _pointsResponse_data_ === void 0 ? void 0 : _pointsResponse_data_.totalRecords) !== null && _pointsResponse_data__totalRecords !== void 0 ? _pointsResponse_data__totalRecords : 0) !== pickupPoints.length;
    const loadMore = (0, _react.useCallback)(async ()=>{
        if (pointsResponse.isValidating || !hasMore) {
            return;
        }
        await pointsResponse.setSize(pointsResponse.error ? pointsResponse.size : pointsResponse.size + 1);
    }, [
        hasMore,
        pointsResponse
    ]);
    const clusters = (0, _react.useMemo)(()=>{
        if (!clustersResponse.data) {
            return [];
        }
        // First check if selected point is present
        let selectedPointIdx = clustersResponse.data.findIndex((point)=>point.type === 'point' && point.deliveryChannelId === selectedDelivery);
        // Search closest cluster otherwise (cluster boundaries are not correct)
        if (selectedPointIdx === -1 && selectedLocation) {
            const { latitude, longitude } = selectedLocation;
            const toRadians = (deg)=>deg * Math.PI / 180;
            const getDistance = (p)=>{
                const R = 6371; // Earth's radius in km
                const dLat = toRadians(p.latitude - latitude);
                const dLng = toRadians(p.longitude - longitude);
                const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRadians(latitude)) * Math.cos(toRadians(p.latitude)) * Math.sin(dLng / 2) ** 2;
                return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
            };
            let closestDistance = Infinity;
            for(let i = 0; i < clustersResponse.data.length; i++){
                const entry = clustersResponse.data[i];
                if ((entry === null || entry === void 0 ? void 0 : entry.type) !== 'cluster') {
                    continue;
                }
                const distance = getDistance(entry);
                if (distance < closestDistance) {
                    closestDistance = distance;
                    selectedPointIdx = i;
                }
            }
        }
        return clustersResponse.data.map((point, idx)=>({
                ...point,
                isSelected: selectedPointIdx === idx
            })).sort((a, b)=>a.isSelected ? -1 : b.isSelected ? 1 : 0); // Sort selected to end so it always shows up on top
    }, [
        clustersResponse.data,
        selectedDelivery,
        selectedLocation
    ]);
    return (0, _react.useMemo)(()=>({
            pickupPoints,
            clusters,
            isLoading: pointsResponse.isLoading,
            isValidating: pointsResponse.isValidating,
            error: pointsResponse.error,
            hasMore,
            loadMore,
            searchTerm,
            setSearchTerm
        }), [
        pickupPoints,
        clusters,
        pointsResponse.isLoading,
        pointsResponse.isValidating,
        pointsResponse.error,
        hasMore,
        loadMore,
        searchTerm,
        setSearchTerm
    ]);
};
const pickupPointsContext = /*#__PURE__*/ (0, _react.createContext)(undefined);
const PickupPointsProvider = (param)=>{
    let { enabled, children } = param;
    return /*#__PURE__*/ (0, _jsxRuntime.jsx)(pickupPointsContext.Provider, {
        value: usePickupPointsApi(enabled),
        children: children
    });
};
exports.PickupPointsProvider = PickupPointsProvider;
const usePickupPoints = ()=>{
    const ctx = (0, _react.useContext)(pickupPointsContext);
    if (!ctx) {
        throw new Error('usePickupPoints must be used within a PickupPointsProvider');
    }
    return ctx;
};
exports.usePickupPoints = usePickupPoints;
